import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './PageRequests.css';
import {FaTrash} from "react-icons/fa";

const PageRequests = () => {
  const [pageRequests, setPageRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approvingIds, setApprovingIds] = useState(new Set());
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deletingIds, setDeletingIds] = useState(new Set());

  // Redirect if not super admin
  useEffect(() => {
    if (user && user.user_type !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchPageRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pages/?skip=0&limit=100');
      const data = await response.data;
      setPageRequests(Array.isArray(data) ? data : data.items || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching page requests:', err);
      setError('Failed to load page requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) {
      return;
    }

    try {
      setDeletingIds(prev => new Set([...prev, pageId]));
      await api.delete(`/pages/${pageId}`);
      // Refresh the page requests list after successful deletion
      await fetchPageRequests();
      alert('Page deleted successfully!');
    } catch (err) {
      console.error('Error deleting page:', err);
      alert('Failed to delete page. Please try again.');
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(pageId);
        return newSet;
      });
    }
  }

  const handleApprovePage = async (pageId) => {
    if (!window.confirm('Are you sure you want to approve this page request?')) {
      return;
    }

    try {
      setApprovingIds(prev => new Set([...prev, pageId]));
      await api.post(`/pages/${pageId}/approve`);
      // Remove approved page from the list
      setPageRequests(prev => prev.filter(page => page.id !== pageId));
      // Show success message
      alert('Page request approved successfully!');
    } catch (err) {
      console.error('Error approving page:', err);
      alert('Failed to approve page request. Please try again.');
    } finally {
      setApprovingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(pageId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    if (user && user.user_type === 'super_admin') {
        fetchPageRequests();
    }
  }, [user]);

  if (!user || user.user_type !== 'super_admin') {
    return (
      <div className="page-requests-container">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Super admin privileges required to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-requests-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading page requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-requests-container">
        <div className="error-state">
          <h2>Error Loading Page Requests</h2>
          <p>{error}</p>
          <button onClick={fetchPageRequests} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-requests-container">
      <div className="page-requests-header">
        <h1>Page Requests</h1>
        <p>Review and approve pending page creation requests</p>
        <button 
          onClick={() => navigate('/dashboard/super-admin')} 
          className="back-btn"
        >
          ← Back to Dashboard
        </button>
      </div>

      {pageRequests.length === 0 ? (
        <div className="empty-state">
          <h3>No Page Requests</h3>
          <p>There are currently no pending page requests to review.</p>
        </div>
      ) : (
        <div className="requests-table-container">
          <div className="requests-count">
            <span>{pageRequests.length} pending request{pageRequests.length !== 1 ? 's' : ''}</span>
          </div>
          
          <div className="table-wrapper">
            <table className="requests-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Working Style</th>
                  <th>Ever Sued</th>
                  <th>Contact Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Supporting Information</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRequests.map((request) => (
                  <tr key={request.id}>
                    <td className="title-cell">
                      <div className="title-content">
                        {request.title || 'No title'}
                      </div>
                    </td>
                    <td className="description-cell">
                      <div className="description-content">
                        {request.description || 'No description'}
                      </div>
                    </td>
                    <td className="working-style-cell">
                      {request.working_style || 'Not specified'}
                    </td>
                    <td className="ever-sued-cell">
                      <span className={`sued-badge ${request.ever_sued ? 'sued-yes' : 'sued-no'}`}>
                        {request.ever_sued ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="contact-name-cell">{request.creator.first_name} &nbsp; {request.creator.last_name} </td>
                    <td className="email-cell">{request.creator.email} </td>
                    <td className="phone-cell">{request.creator.phone_number} </td>
                    <td className="supporting-info-cell">
                      <div className="supporting-content">
                        {request.supporting_information || 'None provided'}
                      </div>
                    </td>
                    <td className="actions-cell">
                      <div className="actions-container">
                        {request.is_approved ? (
                            <span className="approved-status">✅ Approved</span>
                        ) : (
                            <button
                                onClick={() => handleApprovePage(request.id)}
                                disabled={approvingIds.has(request.id)}
                                className="approve-btn"
                            >
                              {approvingIds.has(request.id) ? 'Approving...' : 'Approve'}
                            </button>
                        )}
                        <button
                            onClick={() => handleDeletePage(request.id)}
                            className="delete-btn"
                            disabled={deletingIds.has(request.id)}
                            title="Delete page"
                        >
                          {deletingIds.has(request.id) ? '...' : <FaTrash />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageRequests;